import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { FetchProjectDetailsRequest, FetchProjectMembersRequest } from '../../../core/projects.actions';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { projectDetails, siteWiseDailyReportsSummary } from '../../../core/projects.selectors';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';

import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/services';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { appDataDetails } from '../../../../../shared/core/shared.selectors';
import { projectForRoute, resolveWidgetConfig } from '../../../../../shared/helpers/project-widget-config';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { CreateNewFolderComponent } from '../components/tab-screens/documents/create-new-folder/create-new-folder.component';
import { CommonService } from '../../../../../shared/services/common.service';
import { Ability, subject } from '@casl/ability';

class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }
}

/** The project-details screen's project fetch (also used by the fullscreen map, where that screen isn't rendered). */
export const PROJECT_DETAILS_SCREEN_INCLUDE = [
  'cover_photo',
  'owner_organization',
  { sites: ['geometry'] },
  'material_types',
  'gauge_numbers',
  'material_sources',
  'sub_contractors',
  {
    bidSheet: [
      {
        bidItems: [
          { bid_item_sub_contractor: ['sub_contractor'] },
        ],
      },
    ],
  },
];

@Component({
    selector: 'app-project-details-screen',
    templateUrl: './project-details-screen.component.html',
    styleUrls: ['./project-details-screen.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: 'DD/MM/YYYY' },
                display: {
                    dateInput: 'DD/MM/YYYY',
                    monthYearLabel: 'MMMM YYYY',
                    dateA11yLabel: 'DD/MM/YYYY',
                    monthYearA11yLabel: 'MM YYYY',
                },
            },
        },
    ],
    standalone: false
})
export class ProjectDetailsScreenComponent implements OnInit, OnDestroy {
  projectId: string;
  projectDetails: any;
  currentUser: any;
  dailyReports: any[] = [];
  defaultDateValue = 'All time';
  isContractorMode: boolean;
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 1000,
  };
  sitesCount = 0;
  displayActivityCodes: boolean;
  displayFilter: boolean;
  displayAction: boolean;
  canEditDocuments = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private authService: AuthService,
    private cdRef: ChangeDetectorRef,
    public dialog: MatDialog,
    public commonService: CommonService,
    private ability: Ability,
    private analyticsService: AnalyticsService,
  ) {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.canEditDocuments = this.ability.can('create', subject('Document', { project_id: this.projectId }));
    this.currentUser = this.authService?.getCurrentUser() || {};
    this.isContractorMode = this.currentUser.roles.some(r => r.project_id === this.projectId && (r.role === 'contractor-pm' || r.role === 'contractor-inspector'));
  }

  ngOnInit() {
    // CASL rules are computed at login. Project-level permission flags (e.g. the
    // creator-only report restriction) can change while a session is open, so
    // pull fresh rules whenever a project is entered.
    this.authService.refreshAbilities();

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.fs === undefined) {
          this.displayFilter = qp.tab === 'sites';
          this.displayAction = qp.tab === 'documents';
          // Re-evaluate permission when tab changes so Edge/other browsers get correct visibility after async ability init
          this.canEditDocuments = this.ability.can('create', subject('Document', { project_id: this.projectId }));
          this.cdRef.detectChanges();
        }
      });

    const payload = {
      projectId: this.projectId,
      qp: {
        include: PROJECT_DETAILS_SCREEN_INCLUDE,
      },
    };

    this.store.dispatch(FetchProjectDetailsRequest({ payload }));
    this.store.dispatch(FetchProjectMembersRequest({
      payload: {
        projectId: this.route.snapshot.params.projectId,
      },
    }));

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectDetails = data;
        if (data?.id && data?.name) {
          this.analyticsService.setProjectContext(data.id, data.name);
        }
      });

    this.store.select(siteWiseDailyReportsSummary)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.sitesCount = data?.length || 0;

        this.cdRef.detectChanges();
      });

    // Activity IDs replace the pay app and bid sheet tabs where the daily
    // report records activity codes. That follows this project's widgets: its
    // own widget_config when it has one, the org's otherwise. Either of
    // appdata and the project can land first; the store's project only counts
    // once it is the one in the url.
    combineLatest([this.store.select(appDataDetails), this.store.select(projectDetails)])
      .pipe(takeUntil(this.onDestroy))
      .subscribe(([appData, project]) => {
        const data = resolveWidgetConfig(appData, projectForRoute(project, this.projectId));

        if (data.length > 0) {
          const index = data.findIndex(obj => obj.name === 'activity_codes');
          this.displayActivityCodes = index !== -1;
        }
      });
  }

  createFolder() {
    this.dialog.open(CreateNewFolderComponent, {
      disableClose: false,
      width: '460px',
      height: '445px',
      data: {
        projectId: this.projectId,
      },
    });
  }

  addFile() {
    this.commonService.triggerAddDocument.next(true);
  }

  ngOnDestroy() {
    this.analyticsService.clearProjectContext();
    this.cdRef.detach();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
